/**
 * Thematic raster layers — spec §F5.2 (`layersOn`) + §D5 (`tile_template` / `wms`) +
 * §F5.1 (`rasterPane`, z350: under the choropleth, the boundaries and the vectors).
 *
 * One `<RasterLayer>` per switched-on raster; each owns its `L.TileLayer` and removes it
 * in the effect's cleanup, so switching off, changing role (the catalogue re-fetches) or
 * leaving the map leaves nothing on the canvas.
 *
 * Three kinds reach Leaflet differently:
 *   - `cog_titiler` — the §D5 template **verbatim**. Leaflet substitutes `{z}/{x}/{y}` and
 *     sends the rest of the query byte-for-byte, which is the contract the §I2 nginx
 *     allow-list depends on: it rejects any `%` inside `/tiles/`, so encoding the `,` of
 *     `rescale` or the `/` of the COG path turns a working tile into a 403 (Task 2, NOTES
 *     #247). Never wrap the template in `encodeURI`.
 *   - `wms_external` with `params` — `L.tileLayer.wms` against the §B3 proxy URL, with the
 *     server's `params` spread in.
 *   - `wms_external` with `wms.template` — a **RESTful WMTS** upstream (NASA GIBS), whose
 *     coordinates live in the path. `L.tileLayer(url + template)`, because `L.tileLayer.wms`
 *     would append a GetMap query the server has no route for.
 *
 * **§1a rule 4 (docs/DATA_SOURCES.md).** A `display_only` row is live third-party imagery
 * that is never stored, so it cannot be pre-clipped to the programme. Two controls stand in:
 * the caching proxy refuses tiles that do not intersect the programme bbox
 * (`apps.datasets.wms_bounds`), and `useProgrammeClip` masks what is left to the programme
 * outline. Both are needed — the proxy works in whole tiles, and one z6 tile touching
 * Burkina also covers most of Ghana.
 *
 * Missing pixels are silent. Most seeded `cog_titiler` rows still point at a COG no
 * harvester has written yet (only `soc`, `ph` and `chirps` are published), so TiTiler
 * answers 404/500 for them; `errorTileUrl` swaps in a transparent 1×1 rather than letting
 * Leaflet paint its broken-image glyph across the Sahel. The `unavailable` rows never get
 * this far — §F5.2 disables their checkbox. A display-only tile refused by the §1a gate is
 * the same story: a 403 is a transparent pixel, not a broken image.
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import L from 'leaflet';

import { fetchLayers, fetchProgrammeExtent, queryKeys, type Layer } from '../../lib/api';
import { DEFAULT_LAYER_OPACITY, useAppStore } from '../../lib/state';
import { isRasterLayer, useRasterRegistry } from './layerKinds';
import { useMap } from './mapContext';
import { MAX_ZOOM } from './panes';
import { useProgrammeClip, type LonLatRing } from './ProgrammeClip';
import { useScopeClip } from './ScopeClip';

/** 1×1 fully transparent PNG — the "this tile does not exist" pixel. */
export const TRANSPARENT_TILE =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';

function RasterLayer({ layer, rings }: { layer: Layer; rings?: LonLatRing[] }) {
  const map = useMap();
  const opacity = useAppStore((s) => s.layerOpacity[layer.id] ?? DEFAULT_LAYER_OPACITY);
  // State, not a ref: `useProgrammeClip` has to re-run when the layer is (re)created, and a
  // ref mutation does not re-render. One `setState` per layer creation, not per tile.
  const [tileLayer, setTileLayer] = useState<L.TileLayer | null>(null);

  // Creation depends on the *URL*, not on the opacity: a slider drag must not tear the
  // tile layer down and re-request every tile. Opacity is pushed in the second effect.
  const template = layer.tile_template;
  const wmsUrl = layer.wms?.url;
  const wmsParams = layer.wms?.params;
  const wmsTemplate = layer.wms?.template;
  const wmsMaxZoom = layer.wms?.max_zoom;
  const attribution = layer.attribution ?? undefined;

  useEffect(() => {
    let tile: L.TileLayer | null = null;
    const common = {
      pane: 'rasterPane',
      // The programme COGs are ~1 km (Task 2, NOTES #241); past z12 TiTiler would be asked
      // to upsample a pixel the source does not have. Leaflet stretches the z12 tile
      // instead — same picture, a quarter of the requests. An external upstream says how
      // deep it goes in `wms.max_zoom` (GIBS' matrix set stops at 9), and past that a
      // request is a 404 per tile rather than more detail.
      maxNativeZoom: wmsMaxZoom ?? MAX_ZOOM,
      tileSize: 256,
      errorTileUrl: TRANSPARENT_TILE,
      crossOrigin: true as const,
      // A CC-BY condition, not decoration: Leaflet renders it in the map's attribution
      // control for as long as the layer is on, and drops it when the layer is removed.
      ...(attribution ? { attribution } : {}),
    };
    if (layer.kind === 'cog_titiler' && template) {
      tile = L.tileLayer(template, common);
    } else if (layer.kind === 'wms_external' && wmsUrl) {
      tile = wmsTemplate
        ? L.tileLayer(`${wmsUrl}${wmsTemplate}`, common)
        : L.tileLayer.wms(wmsUrl, {
            format: 'image/png',
            transparent: true,
            ...(wmsParams ?? {}),
            ...common,
          });
    }
    if (!tile) return;
    tile.addTo(map);
    setTileLayer(tile);
    return () => {
      map.removeLayer(tile);
      setTileLayer(null);
    };
  }, [map, layer.kind, template, wmsUrl, wmsParams, wmsTemplate, wmsMaxZoom, attribution]);

  useEffect(() => {
    tileLayer?.setOpacity(opacity);
  }, [tileLayer, opacity]);

  // §1a rule 4 — the programme-outline mask, on this layer's own container so it composes
  // with §G6's AOI clip and swipe instead of fighting them for `pane.style.clipPath`.
  useProgrammeClip(map, tileLayer, rings, layer.display_only);

  return null;
}

export function RasterLayers() {
  const map = useMap();
  const role = useAppStore((s) => s.role);
  const layersOn = useAppStore((s) => s.layersOn);
  const { data: layers } = useQuery({ queryKey: queryKeys.layers(role), queryFn: fetchLayers });

  // Client review R4: this component is mounted for the whole life of the map and already
  // holds the catalogue, so it is where the store learns which ids are rasters.
  useRasterRegistry(layers);

  const drawn = (layers ?? []).filter((l) => layersOn.has(l.id) && isRasterLayer(l));
  const needsOutline = drawn.some((l) => l.display_only);

  // Client round 4 E4: while the §F5.8 filter is on, the raster pane is cut to the scoped
  // unit's outline. Driven from here because "is a raster actually drawn" is this component's
  // own answer — see `ScopeClip.ts`, which owns the geometry, the precedence and the events.
  useScopeClip(map, drawn.length > 0);

  // Fetched only when a display-only layer is actually on: the outline is 13 kB of geometry
  // that a reader who never switches one on has no use for. `staleTime` is generous because
  // the extent moves only when a boundary version is approved (`/meta/programme-extent` is
  // served with `max-age=3600` for the same reason).
  const { data: extent } = useQuery({
    queryKey: queryKeys.programmeExtent(),
    queryFn: fetchProgrammeExtent,
    enabled: needsOutline,
    staleTime: 60 * 60 * 1000,
  });

  return (
    <>
      {drawn.map((l) => (
        <RasterLayer key={l.id} layer={l} rings={extent?.rings as LonLatRing[] | undefined} />
      ))}
    </>
  );
}

export default RasterLayers;
